'use client';

import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { tv } from 'tailwind-variants';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Link } from '@/components/ui/link/link.component';
import { shouldHideNav } from '@/utils/navbar.util';
import { HeaderNavItem } from '@/app/(main)/_components/sign-in-modal/_types/header.types';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

const HOVER_OPEN_DELAY_MS = 100;

const container = tv({
  slots: {
    iconStyles: [
      'ml-1.5 shrink-0 transition-transform duration-200 text-gray-600 w-5 h-5',
    ],
  },
  variants: {
    open: {
      true: {
        iconStyles: ['text-success-700'],
      },
    },
  },
});

export function HeaderNavbarItem({
  normalLabel,
  responsivenessLabel,
  url,
  subItems,
  id,
  target,
  dataTestId,
  dropdownDataTestId,
}: HeaderNavItem) {
  const [open, setOpen] = useState(false);
  const { iconStyles } = container({ open });
  const pathname = usePathname();
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const cancelScheduledOpen = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = undefined;
    }
  }, []);

  useEffect(() => cancelScheduledOpen, [cancelScheduledOpen]);

  const handleMouseEnter = () => {
    cancelScheduledOpen();
    openTimeoutRef.current = setTimeout(() => setOpen(true), HOVER_OPEN_DELAY_MS);
  };

  const handleMouseLeave = () => {
    cancelScheduledOpen();
    setOpen(false);
  };

  if (shouldHideNav(pathname)) {
    return null;
  }

  return (
    <>
      {url && (
        <a
          className="content-center cursor-pointer"
          href={url}
          data-testid={`${id}-navlink`}
          target={target}
        >
          <div>
            <span className="xl:hidden">{responsivenessLabel}</span>
            <span className="hidden xl:inline">{normalLabel}</span>
          </div>
        </a>
      )}
      {!url && (
        <div
          className="h-full content-center relative"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <DropdownMenu open={open} modal={false}>
            <DropdownMenuTrigger
              asChild
              onClick={(event) => event.stopPropagation()}
              className="
                border-2 border-transparent
                text-gray-800
                data-[state=open]:border-b-success-500
                content-center
                cursor-pointer
                [&[data-state=open]>svg]:rotate-180
                transition-all
                data-[state=open]:text-success-700
                h-full
                px-1 md:pl-4 md:pr-3
              "
              role="button"
              aria-controls="dropdown-menu"
              data-testid={dataTestId}
            >
              <button className="flex items-center whitespace-nowrap">
                <span className="xl:hidden">{responsivenessLabel}</span>
                <span className="hidden xl:inline">{normalLabel}</span>
                <FontAwesomeIcon icon={faAngleDown} className={iconStyles()} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-[18.75rem] p-0 rounded-b-xl data-[state=open]:animate-in data-[state=closed]:animate-out"
              data-testid={dropdownDataTestId}
            >
              {subItems.map((subItem) =>
                subItem.onClick
                  ? (
                      <DropdownMenuItem
                        key={subItem.id}
                        data-testid={subItem.dataTestId}
                        data-analytics-event={AnalyticsEnum.CLICK_HEADER_NAV_ITEM}
                        data-analytics-properties={subItem['data-analytics-properties']}
                        className="hover:cursor-pointer px-6 py-[10px] text-gray-800 text-lg hover:rounded-none"
                        onClick={subItem.onClick}
                      >
                        {subItem.label}
                      </DropdownMenuItem>
                    )
                  : (
                      <Link
                        prefetch={false}
                        key={subItem.id}
                        href={subItem.url ?? '/'}
                        target={subItem.target}
                        data-testid={subItem.dataTestId}
                        data-analytics-event={AnalyticsEnum.CLICK_HEADER_NAV_ITEM}
                        data-analytics-properties={subItem['data-analytics-properties']}
                      >
                        <DropdownMenuItem className="hover:cursor-pointer px-6 py-[10px] text-gray-800 text-lg hover:rounded-none">
                          {subItem.label}
                        </DropdownMenuItem>
                      </Link>
                    ),
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </>
  );
}
